import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Bell, X, CheckCheck, GraduationCap, CreditCard, Percent, BookOpen, Building, Server, Search, Plus } from "lucide-react";
import { notificationApi, NotificationItem } from "../../services/notificationApi";

const CATEGORY_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  academic: { label: "Academic", icon: <GraduationCap className="w-3.5 h-3.5" />, color: "text-indigo-400 bg-indigo-950 border-indigo-800" },
  lms: { label: "LMS", icon: <BookOpen className="w-3.5 h-3.5" />, color: "text-sky-400 bg-sky-950 border-sky-800" },
  assignments: { label: "Assignments", icon: <GraduationCap className="w-3.5 h-3.5" />, color: "text-amber-400 bg-amber-950 border-amber-800" },
  quizzes: { label: "Quizzes", icon: <Percent className="w-3.5 h-3.5" />, color: "text-purple-400 bg-purple-950 border-purple-800" },
  finance: { label: "Finance", icon: <CreditCard className="w-3.5 h-3.5" />, color: "text-emerald-400 bg-emerald-950 border-emerald-800" },
  attendance: { label: "Attendance", icon: <Percent className="w-3.5 h-3.5" />, color: "text-amber-400 bg-amber-950 border-amber-800" },
  library: { label: "Library", icon: <BookOpen className="w-3.5 h-3.5" />, color: "text-yellow-400 bg-yellow-950 border-yellow-800" },
  hostel: { label: "Hostel", icon: <Building className="w-3.5 h-3.5" />, color: "text-purple-400 bg-purple-950 border-purple-800" },
  system: { label: "System", icon: <Server className="w-3.5 h-3.5" />, color: "text-slate-400 bg-slate-900 border-slate-700" },
};

const DEFAULT_CFG = { label: "General", icon: <Bell className="w-3.5 h-3.5" />, color: "text-indigo-400 bg-indigo-950 border-indigo-800" };

interface NotificationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NotificationDrawer: React.FC<NotificationDrawerProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const loadInbox = async () => {
    try {
      const data = await notificationApi.getInbox();
      setNotifications(data || []);
    } catch (err) {
      console.error("Failed to load drawer notifications:", err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadInbox();
    }
  }, [isOpen]);

  const unreadCount = notifications.filter((n) => n.status === "unread").length;

  const markAllRead = async () => {
    try {
      await notificationApi.markAllRead();
      loadInbox();
    } catch (err) {
      console.error("Failed to mark all read:", err);
    }
  };

  const markRead = async (id: number) => {
    try {
      await notificationApi.markRead(id);
      loadInbox();
    } catch (err) {
      console.error("Failed to mark notification read:", err);
    }
  };

  const handleItemClick = async (notif: NotificationItem) => {
    if (notif.status === "unread") {
      await markRead(notif.id);
    }
    if (notif.action_url) {
      onClose();
      navigate(notif.action_url);
    }
  };

  const filtered = notifications.filter((n) => {
    const matchesCategory = activeFilter === "all" || n.category === activeFilter;
    const matchesSearch = !searchQuery || n.title.toLowerCase().includes(searchQuery.toLowerCase()) || n.message.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const FILTERS = ["all", "lms", "assignments", "quizzes", "academic", "finance", "attendance", "library", "hostel", "system"];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full max-w-sm bg-slate-950 border-l border-slate-800 z-50 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="p-5 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-indigo-400" />
                <span className="text-sm font-bold text-white">Notification Center</span>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 text-[10px] font-mono font-bold bg-indigo-600 text-white rounded-full">
                    {unreadCount} new
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-[10px] text-indigo-400 hover:text-indigo-300 font-semibold flex items-center gap-1"
                  >
                    <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                  </button>
                )}
                <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="px-4 py-3 border-b border-slate-800">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-2.5 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search notifications..."
                  className="w-full pl-8 pr-3 py-2 text-xs bg-slate-900 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Category Filters */}
            <div className="px-4 py-2.5 border-b border-slate-800 flex gap-1.5 overflow-x-auto scrollbar-none">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className={`px-3 py-1 rounded-full text-[10px] font-mono font-bold capitalize shrink-0 border transition-colors ${
                    activeFilter === f
                      ? "bg-indigo-600 text-white border-indigo-500"
                      : "bg-slate-900 text-slate-400 border-slate-800 hover:text-white"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>

            {/* Notification List */}
            <div className="flex-1 overflow-y-auto divide-y divide-slate-800/60">
              {filtered.length === 0 ? (
                <div className="p-8 text-center space-y-3">
                  <Bell className="w-8 h-8 text-slate-700 mx-auto" />
                  <div className="space-y-1">
                    <p className="text-xs font-bold text-slate-300">No Notifications</p>
                    <p className="text-[11px] text-slate-500">Your notification stream is completely empty.</p>
                  </div>
                  <Link
                    to="/notifications/announcements"
                    onClick={onClose}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold shadow-md transition-all"
                  >
                    <Plus className="w-3.5 h-3.5" /> Create Notification
                  </Link>
                </div>
              ) : (
                filtered.map((notif) => {
                  const cfg = CATEGORY_CONFIG[notif.category] || DEFAULT_CFG;
                  const isUnread = notif.status === "unread";
                  return (
                    <button
                      key={notif.id}
                      onClick={() => handleItemClick(notif)}
                      className={`w-full text-left p-4 hover:bg-slate-900/60 transition-colors relative ${isUnread ? "bg-indigo-950/20" : ""}`}
                    >
                      {isUnread && (
                        <span className="absolute right-4 top-4 w-2 h-2 rounded-full bg-indigo-500" />
                      )}
                      <div className="flex items-start gap-3">
                        <span className={`p-1.5 rounded-lg border text-xs ${cfg.color} shrink-0`}>
                          {cfg.icon}
                        </span>
                        <div className="flex-1 min-w-0 space-y-0.5">
                          <div className="flex items-center gap-2">
                            <p className={`text-xs font-bold leading-snug ${!isUnread ? "text-slate-300" : "text-white"}`}>
                              {notif.title}
                            </p>
                            {notif.event_type && (
                              <span className="px-1.5 py-0.2 rounded text-[9px] font-mono font-medium text-slate-400 bg-slate-800 border border-slate-700">
                                {notif.event_type.replace("LMS_", "").replace(/_/g, " ").toLowerCase()}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-400 leading-relaxed line-clamp-2">{notif.message}</p>
                          <p className="text-[10px] font-mono text-slate-600">
                            {notif.created_at ? new Date(notif.created_at).toLocaleString() : "Just now"}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
